import { useEffect, useState } from "react";
import { Menu, X } from "lucide-react";
import { toast } from "sonner";
import { LANG_OPTIONS } from "@/data/content";
import { useLang } from "@/i18n";
import { CTAButton, Logo } from "./ui";
import { cn } from "@/lib/utils";

const CODE_TO_LANG = { EN: "en", KR: "ht" };
const LANG_TO_CODE = { en: "EN", ht: "KR" };

const LanguageSelector = ({ className }) => {
  const { lang, setLang, t } = useLang();
  const active = LANG_TO_CODE[lang];
  const onPick = (o) => {
    if (o.active) {
      setLang(CODE_TO_LANG[o.code]);
    } else {
      toast(t.nav.comingSoon(o.label), { description: t.nav.comingSoonDesc });
    }
  };
  return (
    <div className={cn("inline-flex items-center rounded-full border border-navy-100 bg-white p-0.5", className)} role="group" aria-label="Select language">
      {LANG_OPTIONS.map((o) => (
        <button
          key={o.code}
          type="button"
          onClick={() => onPick(o)}
          aria-pressed={active === o.code}
          title={o.label + (o.active ? "" : " (coming soon)")}
          data-testid={`lang-${o.code.toLowerCase()}`}
          className={cn(
            "px-2.5 py-1 text-xs font-bold rounded-full transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-gold-500",
            active === o.code ? "bg-navy-900 text-white" : "text-charcoal-700/70 hover:text-navy-900"
          )}
        >
          {o.code}
        </button>
      ))}
    </div>
  );
};

export const Navbar = () => {
  const { t } = useLang();
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 24);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  return (
    <header
      data-testid="site-navbar"
      className={cn(
        "fixed top-0 inset-x-0 z-50 transition-colors duration-300",
        scrolled
          ? "bg-white/90 backdrop-blur-md border-b border-navy-100/60 shadow-sm"
          : "bg-white/60 backdrop-blur-sm border-b border-transparent"
      )}
    >
      <div className="max-w-7xl mx-auto px-6 md:px-10 h-20 flex items-center justify-between">
        <Logo />

        <nav className="hidden lg:flex items-center gap-7">
          {t.nav.links.map((l) => (
            <a
              key={l.href}
              href={l.href}
              data-testid={`nav-link-${l.href.slice(1)}`}
              className="text-[15px] font-semibold text-charcoal-700 hover:text-navy-900 transition-colors relative after:absolute after:-bottom-1.5 after:left-0 after:h-0.5 after:w-0 after:bg-gold-500 after:transition-all hover:after:w-full"
            >
              {l.label}
            </a>
          ))}
        </nav>

        <div className="hidden lg:flex items-center gap-4">
          <LanguageSelector />
          <CTAButton href="#contact" data-testid="nav-consultation-btn" className="px-6 py-3 text-[15px]">
            {t.nav.cta}
          </CTAButton>
        </div>

        <button
          className="lg:hidden p-2 text-navy-900"
          onClick={() => setOpen((v) => !v)}
          aria-label="Toggle menu"
          data-testid="mobile-menu-toggle"
        >
          {open ? <X size={26} /> : <Menu size={26} />}
        </button>
      </div>

      {open && (
        <div className="lg:hidden bg-white border-t border-navy-100" data-testid="mobile-menu">
          <div className="px-6 py-5 flex flex-col gap-1">
            {t.nav.links.map((l) => (
              <a
                key={l.href}
                href={l.href}
                onClick={() => setOpen(false)}
                data-testid={`mobile-nav-link-${l.href.slice(1)}`}
                className="py-3 text-lg font-semibold text-charcoal-700 border-b border-navy-100/60"
              >
                {l.label}
              </a>
            ))}
            <div className="mt-4 flex items-center justify-between">
              <span className="text-sm font-semibold text-charcoal-700/70">{t.nav.language}</span>
              <LanguageSelector />
            </div>
            <CTAButton href="#contact" onClick={() => setOpen(false)} data-testid="mobile-menu-cta" className="mt-4 w-full">
              {t.nav.cta}
            </CTAButton>
          </div>
        </div>
      )}
    </header>
  );
};
